import { usesTwoStepEditorWizard } from "../template-library-categories.js";

/**
 * Canonical popup layout IDs for catalog templates and campaign popups.
 *
 * Naming: `{purpose}-{variant}-v{n}` (kebab-case).
 * Stored on `generalSettings.popupLayoutId` (preferred) or legacy `layoutPreset`.
 *
 * Add new premium / fixed layouts here — one registry for editor, preview, server, seeds.
 */

/** @readonly */
export const POPUP_LAYOUT_IDS = Object.freeze({
  /** Legacy drag-and-drop canvas (default when no layout id is set). */
  FREEFORM_DRAGGABLE: "freeform-draggable",
  /** AI-powered product recommendation upsell (stacked modal). */
  AI_RECOMMENDATION_UPSELL: "ai-recommendation-upsell-v1",
  /** Embedded cart drawer offer strip. */
  CART_DRAWER: "cart-drawer-v1",
  /** Embedded product page upsell strip (pre-purchase). */
  PRODUCT_PAGE_UPSELL: "product-page-upsell-v1",
  /** Shoppable video popup (planned). */
  VIDEO_SHOPPABLE: "video-shoppable-v1",
  /** Social proof + upsell combo (planned). */
  SOCIAL_PROOF_UPSELL: "social-proof-upsell-v1",
  /** Premium vertical product recommendation popup (multi-offer swiper). */
  PRODUCT_RECOMMENDATION: "product-recommendation-v1",
  /** Product-page volume discount (quantity breaks / Buy X Get Y). */
  VOLUME_DISCOUNT: "volume-discount-v1",
});

/** Maps deprecated stored ids → canonical id (do not remove without a DB migration). */
export const POPUP_LAYOUT_LEGACY_ALIASES = Object.freeze({
  "ai-conversion-v1": POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL,
  "structured-upsell-v1": POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL,
});

/**
 * @typedef {object} PopupLayoutDefinition
 * @property {string} label Merchant-facing layout name
 * @property {string} [catalogTemplateTitle] Default catalog template title in MongoDB
 * @property {string} [rendererKey] Future dedicated renderer module key
 * @property {boolean} isDraggable
 * @property {boolean} lockDraggable
 * @property {boolean} usesLayoutFlow Uses stacked flex rows instead of absolute canvas
 * @property {string} [configField] generalSettings key for layout options JSON
 * @property {"modal"|"embedded"} [surface]
 * @property {boolean} [showIntegrationsStep] Merchant editor wizard — default true
 * @property {"default"|"delay-only"} [editorTriggerProfile] Trigger panel constraints
 */

/** @type {Record<string, PopupLayoutDefinition>} */
export const POPUP_LAYOUT_DEFINITIONS = Object.freeze({
  [POPUP_LAYOUT_IDS.FREEFORM_DRAGGABLE]: {
    label: "Freeform draggable",
    rendererKey: "freeform-draggable",
    isDraggable: true,
    lockDraggable: false,
    usesLayoutFlow: false,
    surface: "modal",
  },
  [POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL]: {
    label: "AI Recommendation Upsell",
    catalogTemplateTitle: "AI powered Product Upsell",
    rendererKey: "ai-recommendation-upsell",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    configField: "layoutConfig",
    surface: "modal",
    showIntegrationsStep: false,
    editorTriggerProfile: "default",
  },
  [POPUP_LAYOUT_IDS.CART_DRAWER]: {
    label: "Cart drawer",
    catalogTemplateTitle: "Custom Cart Drawer",
    rendererKey: "cart-drawer",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    /** Step-level `cartDrawerLayout` + synced section slots (see custom-cart-drawer-template). */
    configField: "cartDrawerLayout",
    surface: "embedded",
    showIntegrationsStep: false,
  },
  [POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL]: {
    label: "Product page upsell",
    catalogTemplateTitle: "Product Page Upsell",
    rendererKey: "product-page-upsell",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    configField: "productPageUpsellLayout",
    surface: "embedded",
    showIntegrationsStep: false,
    editorTriggerProfile: "default",
  },
  [POPUP_LAYOUT_IDS.VIDEO_SHOPPABLE]: {
    label: "Video shoppable",
    rendererKey: "video-shoppable",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    surface: "modal",
    showIntegrationsStep: false,
  },
  [POPUP_LAYOUT_IDS.SOCIAL_PROOF_UPSELL]: {
    label: "Social proof upsell",
    rendererKey: "social-proof-upsell",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    surface: "modal",
    showIntegrationsStep: false,
  },
  [POPUP_LAYOUT_IDS.PRODUCT_RECOMMENDATION]: {
    label: "Product recommendation",
    catalogTemplateTitle: "Recommendation Popup",
    rendererKey: "product-recommendation",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    configField: "layoutConfig",
    surface: "modal",
    showIntegrationsStep: false,
    editorTriggerProfile: "default",
  },
  [POPUP_LAYOUT_IDS.VOLUME_DISCOUNT]: {
    label: "Volume discount",
    catalogTemplateTitle: "Volume Discount",
    rendererKey: "volume-discount",
    isDraggable: false,
    lockDraggable: true,
    usesLayoutFlow: true,
    configField: "volumeDiscountLayout",
    surface: "embedded",
    showIntegrationsStep: false,
    editorTriggerProfile: "default",
  },
});

const DEFAULT_LAYOUT_ID = POPUP_LAYOUT_IDS.FREEFORM_DRAGGABLE;

/**
 * @param {string | null | undefined} rawId
 * @returns {string}
 */
export function normalizePopupLayoutId(rawId) {
  if (!rawId || typeof rawId !== "string") return DEFAULT_LAYOUT_ID;
  const trimmed = rawId.trim();
  if (!trimmed) return DEFAULT_LAYOUT_ID;
  return POPUP_LAYOUT_LEGACY_ALIASES[trimmed] || trimmed;
}

/**
 * Read layout id from popup or generalSettings (supports legacy fields).
 * @param {{ generalSettings?: object } | object | null | undefined} popupOrGeneralSettings
 * @returns {string}
 */
export function resolvePopupLayoutId(popupOrGeneralSettings) {
  const gs =
    popupOrGeneralSettings?.generalSettings != null
      ? popupOrGeneralSettings.generalSettings
      : popupOrGeneralSettings || {};

  const raw =
    gs.popupLayoutId ||
    gs.layoutPreset ||
    gs.layoutMode ||
    gs.layoutTree?.preset ||
    gs.layoutConfig?.preset ||
    null;

  return normalizePopupLayoutId(raw);
}

/**
 * @param {{ generalSettings?: object } | object | null | undefined} popupOrGeneralSettings
 * @param {string} layoutId Canonical layout id (use POPUP_LAYOUT_IDS)
 */
export function isPopupLayout(popupOrGeneralSettings, layoutId) {
  return resolvePopupLayoutId(popupOrGeneralSettings) === layoutId;
}

/**
 * @param {{ generalSettings?: object } | object | null | undefined} popupOrGeneralSettings
 * @returns {PopupLayoutDefinition}
 */
export function getPopupLayoutDefinition(popupOrGeneralSettings) {
  const id = resolvePopupLayoutId(popupOrGeneralSettings);
  return (
    POPUP_LAYOUT_DEFINITIONS[id] ||
    POPUP_LAYOUT_DEFINITIONS[DEFAULT_LAYOUT_ID]
  );
}

/**
 * Layout-specific options blob (toggles, copy, design variants).
 * Reads `layoutConfig` (preferred) with legacy `layoutTree` fallback.
 * @param {{ generalSettings?: object } | object | null | undefined} popupOrGeneralSettings
 */
export function getPopupLayoutConfig(popupOrGeneralSettings) {
  const gs =
    popupOrGeneralSettings?.generalSettings != null
      ? popupOrGeneralSettings.generalSettings
      : popupOrGeneralSettings || {};
  return gs.layoutConfig || gs.layoutTree || null;
}

/**
 * Whether this popup uses a catalog-locked stacked layout (not freeform drag canvas).
 * @param {{ generalSettings?: object } | object | null | undefined} popupOrGeneralSettings
 */
export function usesCatalogPopupLayout(popupOrGeneralSettings) {
  const layoutId = resolvePopupLayoutId(popupOrGeneralSettings);
  const def = POPUP_LAYOUT_DEFINITIONS[layoutId];
  return Boolean(def?.usesLayoutFlow && layoutId !== DEFAULT_LAYOUT_ID);
}

/**
 * Editor chrome derived from layout registry (wizard steps, trigger panel profile).
 * @param {{ generalSettings?: object } | object | null | undefined} popupOrGeneralSettings
 */
function resolvePopupForEditorProfile(popupOrGeneralSettings) {
  if (!popupOrGeneralSettings || typeof popupOrGeneralSettings !== "object") {
    return { generalSettings: {} };
  }
  if (popupOrGeneralSettings.generalSettings != null) {
    return popupOrGeneralSettings;
  }
  return { generalSettings: popupOrGeneralSettings };
}

export function getPopupLayoutEditorProfile(popupOrGeneralSettings) {
  const popup = resolvePopupForEditorProfile(popupOrGeneralSettings);
  const def = getPopupLayoutDefinition(popup);
  const hideIntegrationsByLayout = def.showIntegrationsStep === false;
  const hideIntegrationsByCategory = usesTwoStepEditorWizard(popup);

  return {
    showIntegrationsStep: !hideIntegrationsByLayout && !hideIntegrationsByCategory,
    editorTriggerProfile: def.editorTriggerProfile || "default",
  };
}

/** @deprecated Use POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL */
export const LEGACY_AI_CONVERSION_PRESET = "ai-conversion-v1";
